import { createTheme, ThemeProvider } from "@mui/material/styles";
import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate 
} from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
// Coming Soon Pages
import {
  TrialComparisonComingSoon,
  EligibilityCheckerComingSoon,
  DocumentManagerComingSoon,
  MedicationTrackerComingSoon,
  LabTrackerComingSoon,
  TrialCalendarComingSoon,
  CostCalculatorComingSoon,
  SecondOpinionComingSoon,
  ForumsComingSoon,
  PatientStoriesComingSoon,
  SupportGroupsComingSoon,
  ExpertQAComingSoon,
  ResearchLibraryComingSoon,
  GuidelinesComingSoon,
  CalculatorsComingSoon,
  FindDoctorComingSoon,
  OrganizationsComingSoon,
  UserManagementComingSoon,
  SystemSettingsComingSoon,
  SecurityComingSoon,
  BatchMatchingComingSoon,
  TrialAlertsComingSoon,
} from "./pages/common/ComingSoonPages";
import ProviderDashboard from "./pages/provider/ProviderDashboard";
import Unauthorized from "./pages/Unauthorized";

import { useAuth0, AuthProvider } from "./hooks/useAuth0";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, CircularProgress } from "@mui/material";
import { initializeApiWithAuth0 } from "./services/api";

// Core Components
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import CareCopilot from './components/copilot/CareCopilot.jsx';
import ProtectedDashboard from './components/ProtectedDashboard';

// Core Pages
import Dashboard from "./pages/Dashboard";
import Appointments from "./pages/Appointments";
import Reports from "./pages/Reports";
import PricingPage from "./pages/Pricingpage.jsx";
import Auth from "./pages/Auth";
import HomePage from "./pages/Home";
import AboutPage from "./pages/AboutPage";
import Diagram from "./pages/diagram";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import SignupPending from "./pages/SignupPending";
import UserVerification from "./pages/UserVerification";
import ManagePractices from "./pages/admin/ManagePractices";
import DebugAuth from "./components/Auth/DebugAuth";
import ContactPage from "./pages/ContactPage";
import ProfilePage from "./pages/ProfilePage";

// Legal Pages
import PrivacyPage from "./pages/legal/PrivacyPage";
import TermsPage from "./pages/legal/TermsPage";

// Admin Pages
import AdminUserManagement from './pages/admin/AdminUserManagement.jsx';
import ApiUsageDashboard from "./pages/admin/ApiUsageDashboard";
import TrialFormPage from "./pages/admin/TrialFormPage";
import TrialEditPage from "./pages/admin/TrialEditPage";
import TrialImportPage from "./pages/admin/TrialImportPage.jsx";
import EligibilityTranslationPage from "./components/health/EligibilityTranslationPage.jsx";
import ProviderAdminPage from "./pages/admin/ProviderAdminPage.jsx"

// Education Pages
import EducationShell from "./components/education/EducationShell";
import EducationLanding from "./pages/education/EducationLanding";
import CKDComplicationsHub from "./pages/education/CKDComplicationsHub";
import TreatmentPage from "./pages/education/TreatmentPage";
import BiomarkerCalculator from "./pages/education/BiomarkerCalculator";
import CardiovascularComplication from "./pages/education/organs/CardiovascularComplication";
import NeurologicalComplication from "./pages/education/organs/NeurologicalComplication";
import GIComplication from "./pages/education/organs/GIComplication";
import BoneMineralComplication from "./pages/education/organs/BoneMineralComplication";
import AnemiaComplication from "./pages/education/organs/AnemiaComplication";
import FluidElectrolyteComplication from "./pages/education/organs/FluidElectrolyteComplication";
import TypesLanding from "./pages/education/TypesLanding";
import DiabeticNephropathy from "./pages/education/types/DiabeticNephropathy";
import HypertensiveNephrosclerosis from "./pages/education/types/HypertensiveNephrosclerosis";
import Glomerulonephritis from "./pages/education/types/Glomerulonephritis";
import PolycysticKidneyDisease from "./pages/education/types/PolycysticKidneyDisease";
import OtherCauses from "./pages/education/types/OtherCauses";
import LabsGuidePage from "./pages/education/LabsGuidePage";
import LivingWithCKD from "./pages/education/LivingWithCKD";

// Clinical Trials
import ClinicalTrialsSearch from "./components/ClinicalTrials/ClinicalTrialsSearch";
import PatientMatchingPage from "./components/ClinicalTrials/PatientMatchingPage";
import TrialBrowsePage from "./pages/trials/TrialBrowsePage";
import TrialComparisonPage from "./pages/tools/TrialComparisonPage";
import NLPTrialSearch from "./components/ClinicalTrials/NLPTrialSearch";
import TestRoles from "./components/TestRoles";

// Patient Pages
import PatientListPage from "./pages/patients/PatientListPage";
import PatientProfilePage from "./pages/patients/PatientProfilePage";
import TrialMatchingPage from "./pages/patients/TrialMatchingPage";
import DailyIntakeForm from "./pages/patients/DailyIntakeForm";
import DailyIntakeHistory from "./pages/patients/DailyIntakeHistory";
import DailyIntakeDetail from "./pages/patients/DailyIntakeDetail";
import DailyIntakeReport from "./pages/patients/DailyIntakeReport";
import PatientCreatePage from "./pages/patients/PatientCreatePage";
import PatientEditPage from "./pages/patients/PatientEditPage";
import MyCarePlanPage from "./pages/patients/MyCarePlanPage";   // under the DailyIntakeForm import
//accbf pages
import AccbfPage from "./pages/accbf/AccbfPage";

// Tools
import GlossaryPage from "./pages/tools/GlossaryPage";
import EligibilityCheckerPage from "./pages/tools/EligibilityCheckerPage";
import CostCalculatorPage from "./pages/tools/CostCalculatorPage";
import DocumentManagerPage from "./pages/tools/DocumentManagerPage";
import MedicationTrackerPage from "./pages/tools/MedicationTrackerPage";
import LabTrackerPage from "./pages/tools/LabTrackerPage";
import TrialCalendarPage from "./pages/tools/TrialCalendarPage";
import SecondOpinionPage from "./pages/tools/SecondOpinionPage";

// Resources
import ResearchLibraryPage from "./pages/resources/ResearchLibraryPage";
import TreatmentGuidelinesPage from "./pages/resources/TreatmentGuidelinesPage.jsx";
import ClinicalCalculatorsPage from "./pages/resources/ClinicalCalculatorsPage.jsx";
import SupportOrganizations from "./pages/resources/SupportOrganizations";
import DownloadableGuides from "./pages/resources/DownloadableGuides.jsx";

// Health
import DoctorDiscussionGuide from './components/health/DoctorDiscussionGuide.jsx';
import TreatmentPathwayMap from "./components/health/TreatmentPathwayMap.jsx";
import ProviderFinder from "./components/health/ProviderFinder";

// Travel
import TravelBookingPage from "./pages/TravelBookingPage";
import BookingsList from './pages/BookingsList';
import BookingDetails from './pages/BookingDetails';
import TravelSearchPage from './components/travel/TravelSearchPage.jsx';
import HotelSearchForm from './components/travel/HotelSearchForm.jsx';
import CheckoutPage from './components/travel/CheckoutPage.jsx';
import PaymentPage from './components/travel/PaymentPage.jsx';

// Epic Health Records Connect
import EpicCallbackPage from "./pages/EpicCallbackPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import { useEpicConnect } from "./hooks/useEpicConnect";

import './App.css';

/**
 * Blocks a route entirely on native iOS builds, redirecting to home.
 *
 * Why: /pricing and /checkout drive a premium subscription flow that
 * isn't actually functional yet (both explicitly marked "TODO:
 * Integrate/Process payment with Stripe" in their source). Even
 * non-functional, a reachable card-entry-style checkout UI for digital
 * subscription content risks an App Store Guideline 3.1.1 rejection
 * (In-App Purchase required for digital content/services sold outside
 * the app's own IAP system). Blocking this on iOS specifically removes
 * that risk without touching the feature for web/Android, where it can
 * keep being built out toward a real StoreKit integration later.
 *
 * This does NOT affect /travel/payment/:id — that's a separate, fully
 * working Stripe integration for booking real-world travel (flights/
 * hotels), which Apple generally exempts from IAP requirements the same
 * way ride-share and hotel-booking apps are exempt.
 */
function BlockOnIOS({ children }) {
  const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  if (isIOSNative) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// ── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: { main: "#1A5EA8" },
    secondary: { main: "#0B2545" },
  },
  components: {
    MuiToolbar: {
      styleOverrides: { root: { minHeight: "64px !important" } },
    },
  },
});

// ── App Routes ───────────────────────────────────────────────────────────────
function AppRoutes() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (isAuthenticated) {
      initializeApiWithAuth0(getAccessTokenSilently);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center",
        minHeight: "100vh", background: "linear-gradient(135deg,#1A5EA8,#0B2545)",
        color: "white", flexDirection: "column", gap: 2 }}>
        <CircularProgress size={60} sx={{ color: "white" }} />
        <div>Loading NephroTrax...</div>
      </Box>
    );
  }

  return (
    <Routes>

      {/* ══════════════════════════════════════════════════════════════════
          PUBLIC ROUTES — no authentication required
         ══════════════════════════════════════════════════════════════════ */}

      {/* ── Core public pages ──────────────────────────────────────────── */}
      <Route path="/home"    element={<Layout><HomePage /></Layout>} />
      <Route path="/about"   element={<Layout><AboutPage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/pricing" element={<BlockOnIOS><Layout><PricingPage /></Layout></BlockOnIOS>} />
      <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
      <Route path="/hipaa"   element={<Layout><PrivacyPage /></Layout>} />
      <Route path="/terms"   element={<Layout><TermsPage /></Layout>} />
      {/* accbf page route */}
      <Route path="/accbf" element={<AccbfPage />} />

      {/* ── Auth pages ─────────────────────────────────────────────────── */}
      <Route path="/login"          element={<Layout><Auth /></Layout>} />
      <Route path="/signup"         element={<Layout><Signup /></Layout>} />
      <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
      <Route path="/unauthorized" element={<Layout><Unauthorized /></Layout>} />
      <Route path="/signup-pending" element={<Layout><SignupPending /></Layout>} />
      <Route path="/debug-auth"     element={<Layout><DebugAuth /></Layout>} />
      <Route path="/callback"       element={<CallbackPage />} />
      <Route path="/auth/callback"  element={<AuthCallbackPage />} />

      {/* ── Epic health records callback — not wrapped in ProtectedRoute so
          there's no risk of an auth-state loading flicker bouncing the
          patient to /login before the token exchange can run. The patient
          must already have an active NephroTrax session (same browser
          tab / app instance) for the exchange call itself to succeed, since
          it requires their existing Auth0 bearer token. ─────────────────── */}
      <Route path="/epic-callback"  element={<EpicCallbackPage />} />

      {/* ── Profile — public so unauthenticated users can view ─────────── */}
      <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />

      {/* ── Dashboard ──────────────────────────────────────────────────── */}
      <Route path="/dashboard" element={<Layout><ProtectedDashboard /></Layout>} />

      {/* ── Education — fully public ───────────────────────────────────── */}
            <Route path="/education" element={<Layout><Outlet /></Layout>}>
        <Route index element={<EducationLanding />} />
        <Route path="basics"                     element={<CKDComplicationsHub />} />
        <Route path="basics/cardiovascular"      element={<CardiovascularComplication />} />
        <Route path="basics/neuro"               element={<NeurologicalComplication />} />
        <Route path="basics/gi"                  element={<GIComplication />} />
        <Route path="basics/bone-mineral"        element={<BoneMineralComplication />} />
        <Route path="basics/anemia"              element={<AnemiaComplication />} />
        <Route path="basics/fluid-electrolyte"   element={<FluidElectrolyteComplication />} />
        <Route path="treatment"      element={<TreatmentPage />} />
        <Route path="calculator"     element={<BiomarkerCalculator />} />
 
        {/* CKD Causes — flattened from the old 12-type amyloidosis submenu */}
        <Route path="types"                    element={<TypesLanding />} />
        <Route path="types/diabetic"           element={<DiabeticNephropathy />} />
        <Route path="types/hypertensive"       element={<HypertensiveNephrosclerosis />} />
        <Route path="types/glomerulonephritis" element={<Glomerulonephritis />} />
        <Route path="types/pkd"                element={<PolycysticKidneyDisease />} />
        <Route path="types/other"              element={<OtherCauses />} />
      </Route>


      {/* ── Clinical Trials — public search and browse ─────────────────── */}
      <Route path="/trials/search"
        element={<Layout><ClinicalTrialsSearch /></Layout>} />
      <Route path="/trials/browse"
        element={<Layout><TrialBrowsePage /></Layout>} />
      <Route path="/trials/nlp-search"
        element={<Layout><NLPTrialSearch /></Layout>} />

      {/* ── Provider Finder — public search ───────────────────────────── */}
      <Route path="/my-health/find-provider"
        element={<Layout><ProviderFinder /></Layout>} />

      {/* ── Lab Results Guide — public education; was never routed ──────── */}
      <Route path="/learn/labs"
        element={<Layout><LabsGuidePage /></Layout>} />

      {/* ── Living with CKD — public education; was never routed ────────── */}
      <Route path="/learn/living-with-ckd"
        element={<Layout><LivingWithCKD /></Layout>} />

      {/* ── Legacy redirects ───────────────────────────────────────────── */}
      <Route path="/amyloid"         element={<Navigate to="/education" replace />} />
      <Route path="/amyloid/cardiac" element={<Navigate to="/education/basics/cardiovascular" replace />} />
      <Route path="/amyloid/neuro"   element={<Navigate to="/education/basics/neuro" replace />} />
      <Route path="/amyloid/gi"      element={<Navigate to="/education/basics/gi" replace />} />
      <Route path="/amyloid/msk"     element={<Navigate to="/education/basics/bone-mineral" replace />} />
      <Route path="/amyloid/optho"   element={<Navigate to="/education/basics/anemia" replace />} />
      <Route path="/amyloid/renal"   element={<Navigate to="/education/basics/fluid-electrolyte" replace />} />
      <Route path="/amyloid/clinicaltrialsearch" element={<Navigate to="/trials/search" replace />} />

      {/* ══════════════════════════════════════════════════════════════════
          PROTECTED ROUTES — login required
         ══════════════════════════════════════════════════════════════════ */}
      <Route path="/*" element={
        <ProtectedRoute>
          <Layout>
            <Routes>

              {/* ── General ──────────────────────────────────────────────── */}
              <Route path="/appointments" element={<Appointments />} />
              <Route path="/reports"      element={<Reports />} />
              <Route path="/test-roles"   element={<TestRoles />} />
              <Route path="/diagram"      element={<Diagram />} />

              {/* ── Clinical Trials — authenticated features ──────────────── */}
              <Route path="/trials/patientmatching" element={<PatientMatchingPage />} />
              <Route path="/amyloid/patientmatching" element={<Navigate to="/trials/patientmatching" replace />} />
              <Route path="/trials/matched"          element={<ProtectedDashboard />} />
              <Route path="/trials/smart-match"      element={<ProtectedDashboard />} />
              <Route path="/trials/alerts"           element={<TrialAlertsComingSoon />} />
              <Route path="/tools/compare-trials"    element={<TrialComparisonPage />} />

              {/* ── Patient Management ────────────────────────────────────── */}
              <Route path="/patients" element={
                <ProtectedRoute requiredRoles={["Provider","Admin"]}>
                  <PatientListPage />
                </ProtectedRoute>} />
              <Route path="/patients/new"                        element={<PatientCreatePage />} />
              <Route path="/patients/:patientId"                 element={<PatientProfilePage />} />
              <Route path="/patients/edit/:id" element={
                <ProtectedRoute requiredRoles={["Provider","Admin"]}>
                  <PatientEditPage />
                </ProtectedRoute>} />
                <Route path="/patients/:patientId/my-care-plan" element={<MyCarePlanPage />} />   // directly above the match-trials route
              <Route path="/patients/:patientId/match-trials"    element={<TrialMatchingPage />} />
              <Route path="/patients/:patientId/daily-intake"    element={<DailyIntakeForm />} />
              <Route path="/patients/:patientId/daily-intake/history" element={<DailyIntakeHistory />} />
              <Route path="/patients/:patientId/daily-intake/report" element={<DailyIntakeReport />} />
              <Route path="/patients/:patientId/daily-intake/:entryId" element={<DailyIntakeDetail />} />
              
              <Route path="/patients/batch-match"  element={<BatchMatchingComingSoon />} />
              <Route path="/patients/analytics" element={
                <ProtectedRoute requiredRoles={["Provider","Admin"]}>
                  <ProviderDashboard />
                </ProtectedRoute>} />

              {/* ── Health ───────────────────────────────────────────────── */}
              <Route path="/my-health/doctor-discussion-guide" element={<DoctorDiscussionGuide />} />
              <Route path="/my-health/care-copilot"            element={<CareCopilot />} />
              <Route path="/my-health/pathway"                 element={<TreatmentPathwayMap />} />

              {/* ── Tools ────────────────────────────────────────────────── */}
              <Route path="/tools/glossary"        element={<GlossaryPage />} />
              <Route path="/tools/eligibility"     element={<EligibilityCheckerPage />} />
              <Route path="/tools/cost-calculator" element={<CostCalculatorPage />} />
              <Route path="/tools/documents"       element={<DocumentManagerPage />} />
              <Route path="/tools/medications"     element={<MedicationTrackerPage />} />
              <Route path="/tools/lab-tracker"     element={<LabTrackerPage />} />
              <Route path="/tools/calendar"        element={<TrialCalendarPage />} />
              <Route path="/tools/second-opinion"  element={<SecondOpinionPage />} />

              {/* ── Travel ───────────────────────────────────────────────── */}
              <Route path="/tools/travel"       element={<TravelBookingPage />} />
              <Route path="/travel/book"        element={<TravelBookingPage />} />
              <Route path="/travel/bookings"    element={<BookingsList />} />
              <Route path="/travel/bookings/:id" element={<BookingDetails />} />
              <Route path="/travel/search"      element={<TravelSearchPage />} />
              <Route path="/travel/hotel"       element={<HotelSearchForm />} />
              <Route path="/travel/payment/:id" element={<PaymentPage />} />
              <Route path="/checkout"           element={<BlockOnIOS><CheckoutPage /></BlockOnIOS>} />

              {/* ── Resources ────────────────────────────────────────────── */}
              <Route path="/resources/library"       element={<ResearchLibraryPage />} />
              <Route path="/resources/guidelines"    element={<TreatmentGuidelinesPage />} />
              <Route path="/resources/calculators"   element={<ClinicalCalculatorsPage />} />
              <Route path="/resources/guides"        element={<DownloadableGuides />} />
              <Route path="/resources/organizations" element={<SupportOrganizations />} />
              <Route path="/resources/find-doctor"   element={<FindDoctorComingSoon />} />

              {/* ── Community ────────────────────────────────────────────── */}
              <Route path="/community/forums"    element={<ForumsComingSoon />} />
              <Route path="/community/stories"   element={<PatientStoriesComingSoon />} />
              <Route path="/community/participate" element={<ForumsComingSoon />} />
              <Route path="/community/groups"    element={<SupportGroupsComingSoon />} />
              <Route path="/community/expert-qa" element={<ExpertQAComingSoon />} />

              {/* ── Admin ────────────────────────────────────────────────── */}
              <Route path="/admin/trials/import" element={
                <ProtectedRoute requiredRoles={["Admin","Researcher"]}>
                  <TrialImportPage />
                </ProtectedRoute>} />
              <Route path="/admin/trials/edit/:id" element={
                <ProtectedRoute requiredRoles={["Admin","Researcher"]}>
                  <TrialEditPage />
                </ProtectedRoute>} />
              <Route path="/admin/trials/new" element={
                <ProtectedRoute requiredRoles={["Admin","Researcher"]}>
                  <TrialFormPage />
                </ProtectedRoute>} />
              <Route path="/admin/user-verification" element={
                <ProtectedRoute requiredRoles={["Admin"]}>
                  <UserVerification />
                </ProtectedRoute>} />
              <Route path="/admin/manage-practices" element={
                <ProtectedRoute requiredRoles={["Admin"]}>
                  <ManagePractices />
                </ProtectedRoute>} />

                <Route 
                path="/admin/provider/manage" element={
                <ProtectedRoute requiredRoles={["Admin","Researcher"]}>
                  <ProviderAdminPage />
                </ProtectedRoute>}
                />

              <Route path="/admin/users" element={
                <ProtectedRoute requiredRoles={["Admin"]}>
                  <AdminUserManagement />
                </ProtectedRoute>} />
              <Route path="/admin/translations" element={
                <ProtectedRoute requiredRoles={["Admin"]}>
                  <EligibilityTranslationPage />
                </ProtectedRoute>} />
              <Route path="/admin/api-usage" element={
                <ProtectedRoute requiredRoles={["Admin"]}>
                  <ApiUsageDashboard />
                </ProtectedRoute>} />
              <Route path="/admin/settings" element={
                <ProtectedRoute requiredRoles={["Admin"]}>
                  <SystemSettingsComingSoon />
                </ProtectedRoute>} />
              <Route path="/admin/security" element={
                <ProtectedRoute requiredRoles={["Admin"]}>
                  <SecurityComingSoon />
                </ProtectedRoute>} />

            </Routes>
          </Layout>
        </ProtectedRoute>
      } />

      {/* ── Root redirect ──────────────────────────────────────────────── */}
      <Route path="/" element={
        isAuthenticated
          ? <Navigate to="/dashboard" replace />
          : <Navigate to="/home" replace />
      } />

    </Routes>
  );
}

// ── Callback Page ────────────────────────────────────────────────────────────
function CallbackPage() {
  const { isLoading } = useAuth0();
  if (isLoading) {
    return (
      <Box sx={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh" }}>
        <CircularProgress />
        <Box ml={2}>Completing login...</Box>
      </Box>
    );
  }
  return <Navigate to="/dashboard" replace />;
}

const isNative = Capacitor.isNativePlatform();
const CALLBACK_SCHEME = 'com.verainsight.nephrotrax://callback';
const EPIC_CALLBACK_SCHEME = 'com.verainsight.nephrotrax://epic-callback';

function AuthCallbackListener() {
  const { handleRedirectCallback } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative) return;

    let listenerHandle;
    CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
      if (!url.startsWith(CALLBACK_SCHEME)) return;
      try {
        if (url.includes('code=') && url.includes('state=')) {
          await handleRedirectCallback(url);
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('Auth0 native callback error:', err);
      } finally {
        await Browser.close();
      }
    }).then((handle) => { listenerHandle = handle; });

    return () => { listenerHandle?.remove(); };
  }, [handleRedirectCallback, navigate]);

  return null;
}

// ── Epic Health Records Callback Listener ───────────────────────────────────
// Separate from AuthCallbackListener above — different redirect URI scheme
// (epic-callback vs callback), so the two never collide even though both
// listen on the same underlying appUrlOpen event. This must live at the app
// root (not inside ConnectEpicButton) so it's always registered regardless
// of which screen the patient was on when they tapped "Connect my health
// records" and got redirected back.
function EpicCallbackListener() {
  const { handleEpicCallback } = useEpicConnect();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative) return;

    let listenerHandle;
    CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
      if (!url.startsWith(EPIC_CALLBACK_SCHEME)) return;
      try {
        await handleEpicCallback(url);
      } catch (err) {
        console.error('Epic native callback error:', err);
      } finally {
        await Browser.close();
      }
    }).then((handle) => { listenerHandle = handle; });

    return () => { listenerHandle?.remove(); };
  }, [handleEpicCallback, navigate]);

  return null;
}

// ── Main App ─────────────────────────────────────────────────────────────────
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <ScrollToTop />
          <AuthCallbackListener />
          <EpicCallbackListener />
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;